import React from 'react';
import { Link } from 'react-router-dom';
import { SiteLayout, FLAME, INK, BODY, MUTED, BORDER, MONO } from './SiteLayout';

const LAST_UPDATED = 'March 25, 2026';
const COMPANY = 'Ignitor Ads';
const EMAIL = 'IgnitorAds.Support@gmail.com';
const SITE = 'ignitorads.co';

function Section({ title, children }) {
  return (
    <section style={{ marginBottom: '38px' }}>
      <h2 style={{
        fontSize: '22px', fontWeight: 600, color: INK, letterSpacing: '-.4px',
        marginBottom: '14px', paddingBottom: '10px', borderBottom: `1px solid ${BORDER}`,
      }}>{title}</h2>
      <div style={{ fontSize: '16px', color: '#3a352d', lineHeight: 1.8 }}>{children}</div>
    </section>
  );
}
function P({ children }) { return <p style={{ marginBottom: '12px' }}>{children}</p>; }
function UL({ items }) {
  return (
    <ul style={{ paddingLeft: '22px', marginBottom: '12px' }}>
      {items.map((item, i) => <li key={i} style={{ marginBottom: '8px' }}>{item}</li>)}
    </ul>
  );
}
const link = { color: FLAME, fontWeight: 600 };

export default function TermsOfService() {
  return (
    <SiteLayout>

      {/* Hero */}
      <section style={{ maxWidth: '1180px', margin: '0 auto', padding: '80px 32px 24px', textAlign: 'center' }}>
        <p style={{ fontFamily: MONO, fontSize: '16px', letterSpacing: '1.8px', color: FLAME, textTransform: 'uppercase', margin: 0 }}>
          Legal
        </p>
        <h1 className="ia-hero-h1" style={{ fontSize: '52px', fontWeight: 600, letterSpacing: '-1.6px', margin: '18px 0 0' }}>
          Terms of Service
        </h1>
        <p style={{ fontFamily: MONO, fontSize: '14px', color: MUTED, marginTop: '14px' }}>
          Last updated: {LAST_UPDATED}
        </p>
      </section>

      {/* Content */}
      <div style={{ maxWidth: '820px', margin: '0 auto', padding: '30px 32px 80px' }}>

        <div style={{
          background: 'rgba(255,255,255,.85)', border: `1px solid ${BORDER}`,
          borderLeft: `4px solid ${FLAME}`, borderRadius: '14px',
          padding: '20px 24px', marginBottom: '44px',
          fontSize: '16px', color: '#3a352d', lineHeight: 1.7,
        }}>
          <strong>Summary:</strong> By using IgnitorAds, you agree to these terms. We provide AdOps compliance tools
          on a subscription basis. You own your data. We own our platform. Be a good actor. Contact us if you have questions.
        </div>

        <Section title="1. Acceptance of Terms">
          <P>By accessing or using the {COMPANY} platform at {SITE} ("the Service"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree, do not use the Service.</P>
          <P>These Terms apply to all users, including individuals, companies, and organisations accessing the Service on behalf of a business entity.</P>
        </Section>

        <Section title="2. Description of Service">
          <P>{COMPANY} provides a suite of programmatic advertising operations ("AdOps") compliance and management tools, including but not limited to:</P>
          <UL items={[
            'Ads.txt / App-ads.txt validation and crawling',
            'Sellers.json verification',
            'Supply chain (schain) validation',
            'Bid request analysis',
            'Discrepancy checking and monitoring',
            'Bundle tracking and lookup',
            'App category segmentation',
          ]} />
          <P>The Service is provided on a subscription basis. Features available to you depend on your subscription plan (Free or Pro).</P>
        </Section>

        <Section title="3. Account Registration">
          <P>To access Pro features, you must register for an account. You agree to:</P>
          <UL items={[
            'Provide accurate, current, and complete registration information',
            'Maintain the security of your password and accept responsibility for all activity under your account',
            'Notify us immediately at ' + EMAIL + ' of any unauthorised use of your account',
            'Not share your account credentials with any other person or entity',
          ]} />
          <P>We reserve the right to suspend or terminate accounts that violate these Terms.</P>
        </Section>

        <Section title="4. Subscription Plans and Billing">
          <P>The Service offers both a Free tier and paid Pro subscription plans. By subscribing to a Pro plan:</P>
          <UL items={[
            'You authorise us to charge your payment method on a recurring basis',
            'Subscriptions auto-renew unless cancelled before the renewal date',
            'Refunds are handled on a case-by-case basis — contact ' + EMAIL + ' within 7 days of a charge for disputes',
            'We reserve the right to change pricing with 30 days written notice',
          ]} />
        </Section>

        <Section title="5. Acceptable Use">
          <P>You agree to use the Service only for lawful purposes. You must not:</P>
          <UL items={[
            'Use the Service to engage in fraudulent ad traffic, ad fraud, or any deceptive advertising practices',
            'Attempt to reverse engineer, decompile, or extract source code from the platform',
            'Use automated bots or scrapers to bulk-extract data from the platform beyond its intended API',
            'Resell, sublicense, or redistribute the Service or its outputs without written permission',
            'Upload malicious files, data, or content that could harm the platform or other users',
            'Use the Service in any way that violates applicable local, national, or international law',
          ]} />
        </Section>

        <Section title="6. Intellectual Property">
          <P>All content, code, design, algorithms, and functionality of the {COMPANY} platform are the exclusive property of {COMPANY} and are protected by copyright, trademark, and other intellectual property laws.</P>
          <P>You are granted a limited, non-exclusive, non-transferable licence to access and use the Service for your internal business purposes only.</P>
          <P>You retain all rights to the data you upload or input into the Service. By uploading data, you grant us a limited licence to process it solely for the purpose of providing the Service to you.</P>
        </Section>

        <Section title="7. Data and Privacy">
          <P>Your use of the Service is also governed by our <Link to="/privacy" style={link}>Privacy Policy</Link>, which is incorporated into these Terms by reference. By using the Service, you consent to the data practices described in our Privacy Policy.</P>
        </Section>

        <Section title="8. Disclaimers and Limitation of Liability">
          <P>The Service is provided "as is" and "as available" without warranties of any kind, express or implied. {COMPANY} does not warrant that:</P>
          <UL items={[
            'The Service will be uninterrupted, error-free, or completely secure',
            'Results from tools (e.g., ads.txt validation, discrepancy calculations) are 100% accurate or complete',
            'The Service will meet all your specific business requirements',
          ]} />
          <P>To the maximum extent permitted by applicable law, {COMPANY} shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of the Service, even if we have been advised of the possibility of such damages.</P>
          <P>Our total liability to you for any claim arising from use of the Service shall not exceed the total fees paid by you to {COMPANY} in the 3 months preceding the claim.</P>
        </Section>

        <Section title="9. Suspension and Termination">
          <P>We may suspend or terminate your access to the Service at any time, with or without notice, if:</P>
          <UL items={[
            'You violate these Terms',
            'Your account is involved in fraudulent or illegal activity',
            'You fail to pay subscription fees when due',
          ]} />
          <P>You may cancel your subscription at any time through your account settings or by contacting {EMAIL}. Cancellation takes effect at the end of your current billing period.</P>
          <P>Upon termination, your right to access the Service ceases immediately. We will retain your data for 30 days after termination, after which it may be permanently deleted.</P>
        </Section>

        <Section title="10. Changes to Terms">
          <P>We reserve the right to modify these Terms at any time. We will notify you of material changes via email or a notice on the platform. Your continued use of the Service after changes take effect constitutes your acceptance of the new Terms.</P>
        </Section>

        <Section title="11. Governing Law">
          <P>These Terms shall be governed by and construed in accordance with applicable law. Any disputes arising from these Terms or your use of the Service shall first be attempted to be resolved through good-faith negotiation.</P>
        </Section>

        <Section title="12. Contact">
          <P>If you have any questions about these Terms of Service, please contact us at:</P>
          <div style={{
            background: '#efece5', borderRadius: '12px', padding: '20px 24px',
            border: `1px solid ${BORDER}`, fontSize: '15px',
          }}>
            <div style={{ fontWeight: 700, color: INK, marginBottom: '6px' }}>{COMPANY}</div>
            <div>Email: <a href={`mailto:${EMAIL}`} style={link}>{EMAIL}</a></div>
            <div>Website: <a href={`https://${SITE}`} style={link}>{SITE}</a></div>
          </div>
        </Section>

      </div>
    </SiteLayout>
  );
}
